import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Link } from '@mui/material';
import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ActionResultOutcome } from '../../sse/actionResult';
import type { MessageActionResult as MessageActionResultData } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import { useSidekickPanelContext } from '../AISidekickPanel/SidekickPanelContext';
import VisualizationZoomDialog from '../VisualizationZoomDialog';
import { getActionLink } from './actionLinks';
import useStyles from './MessageActionResult.styles';
import VisualizationImageThumbnail from './VisualizationImageThumbnail';

const TRANSLATION_KEYS = {
  mapAlt: 'chat.actionResult.mapAlt',
  expandAriaLabel: 'chat.visualization.preview.expandAriaLabel',
} as const;

// Only an action that changed something has a record worth opening.
const OUTCOMES_WITH_TARGET: ReadonlySet<ActionResultOutcome> = new Set([
  'succeeded',
  'partial',
]);

type MessageActionResultProps = {
  /** The result to render. */
  actionResult: MessageActionResultData;
  /** The answer's own text, so an outcome message it already reads is not repeated. */
  answerContent: string;
};

const isPlainLeftClick = (event: MouseEvent): boolean =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;

/**
 * Renders how an action Sidekick ran ended, under its answer: the outcome
 * message, the map image, and a link to the record the action changed. The
 * link opens through the host's router, and stays a plain link for a new tab
 * or a host without one.
 * @param actionResult - The result to render.
 * @param answerContent - The answer's own text.
 */
const MessageActionResult = ({
  actionResult,
  answerContent,
}: MessageActionResultProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const { onNavigate } = useSidekickPanelContext();
  const [hasMapFailed, setHasMapFailed] = useState(false);
  const [isMapZoomOpen, setIsMapZoomOpen] = useState(false);

  const hasTarget = OUTCOMES_WITH_TARGET.has(actionResult.outcome);
  const message = actionResult.message.trim();
  const note =
    message !== '' && !answerContent.includes(message) ? message : undefined;
  const mapUrl = hasTarget && !hasMapFailed ? actionResult.mapUrl : undefined;
  const link = hasTarget ? getActionLink(actionResult) : undefined;

  if (note === undefined && mapUrl === undefined && link === undefined) {
    return null;
  }

  const mapAlt = t(TRANSLATION_KEYS.mapAlt);
  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (link === undefined || onNavigate === undefined) {
      return;
    }
    if (!isPlainLeftClick(event)) {
      return;
    }
    event.preventDefault();
    onNavigate(link.path);
  };

  return (
    <div
      className={classes.actionResult}
      data-outcome={actionResult.outcome}
      data-testid="ai-sidekick-message-action-result"
    >
      {note !== undefined && <p className={classes.note}>{note}</p>}
      {mapUrl !== undefined && (
        <>
          <VisualizationImageThumbnail
            alt={mapAlt}
            expandAriaLabel={t(TRANSLATION_KEYS.expandAriaLabel, {
              label: mapAlt,
            })}
            onError={() => setHasMapFailed(true)}
            onExpand={() => setIsMapZoomOpen(true)}
            src={mapUrl}
          />
          <VisualizationZoomDialog
            alt={mapAlt}
            onClose={() => setIsMapZoomOpen(false)}
            open={isMapZoomOpen}
            src={mapUrl}
            title={mapAlt}
          />
        </>
      )}
      {link !== undefined && (
        <Link
          className={classes.link}
          data-testid="ai-sidekick-message-action-link"
          href={link.path}
          onClick={handleLinkClick}
          underline="none"
        >
          {t(link.labelKey)}
          <ChevronRightIcon aria-hidden="true" className={classes.linkIcon} />
        </Link>
      )}
    </div>
  );
};

export default MessageActionResult;
